
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import PricingSection from "@/components/subscription/PricingSection";
import SubscriptionStatus from "@/components/subscription/SubscriptionStatus";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, X, Settings } from "lucide-react";
import { isAndroidTWA } from "@/utils/runtimeContext";
import AndroidNeutralNotice from "@/components/legal/AndroidNeutralNotice";
import { useAnalytics } from "@/hooks/useAnalytics";

const PlansPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { track } = useAnalytics();
  const location = useLocation();
  useEffect(() => { track("plan_view"); }, [track]);

  // Scroll automático para a seção de planos quando vier com âncora #planos
  useEffect(() => {
    if (location.hash !== "#planos") return;
    const t = setTimeout(() => {
      document.getElementById("planos")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t);
  }, [location.hash]);

  if (isAndroidTWA()) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <AndroidNeutralNotice />
      </div>
    );
  }

  const plansJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "IA Palpite Guru - Assinatura Premium",
    description:
      "Assinatura do IA Palpite Guru com acesso completo a palpites com IA, 15+ métodos exclusivos, estatísticas em tempo real, resultados Deu no Poste e software turbinado.",
    brand: {
      "@type": "Brand",
      name: "IA Palpite Guru",
    },
    image: "https://aipalpiteguru.mentepoderosa2030.com/pwa-512x512.png",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "1247",
      bestRating: "5",
      worstRating: "1",
    },
    offers: [
      {
        "@type": "Offer",
        name: "Experimente 15 dias",
        price: "27.90",
        priceCurrency: "BRL",
        availability: "https://schema.org/InStock",
        url: "https://aipalpiteguru.mentepoderosa2030.com/planos",
        priceValidUntil: "2026-12-31",
        visible: false,
      },
      {
        "@type": "Offer",
        name: "Plano Mensal",
        price: "44.90",
        priceCurrency: "BRL",
        availability: "https://schema.org/InStock",
        url: "https://aipalpiteguru.mentepoderosa2030.com/planos",
        priceValidUntil: "2026-12-31",
        visible: true,
      },
      {
        "@type": "Offer",
        name: "Plano Trimestral",
        price: "59.90",
        priceCurrency: "BRL",
        availability: "https://schema.org/InStock",
        url: "https://aipalpiteguru.mentepoderosa2030.com/planos",
        priceValidUntil: "2026-12-31",
        visible: true,
      },
      {
        "@type": "Offer",
        name: "Plano Anual",
        price: "189.90",
        priceCurrency: "BRL",
        availability: "https://schema.org/InStock",
        url: "https://aipalpiteguru.mentepoderosa2030.com/planos",
        priceValidUntil: "2026-12-31",
        visible: true,
      },
    ].filter((offer) => offer.visible !== false).map(({ visible, ...offer }) => offer),
  };

  return (
    <>
      <SEOHead
        title="Planos e Preços - IA Palpite Guru"
        description="Escolha entre os planos mensal (R$ 44,90), trimestral (R$ 59,90) e anual (R$ 189,90) do IA Palpite Guru, com palpites por IA, métodos exclusivos e estatísticas."
        keywords="planos palpite guru, assinatura jogo do bicho, preços palpite guru, plano premium bicho, plano vip bicho"
        canonical="/planos"
        jsonLd={plansJsonLd}
      />
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4">Planos e Assinaturas</h1>
          <p className="text-muted-foreground text-lg">
            Escolha o plano perfeito para maximizar seus ganhos no jogo do bicho
          </p>
        </div>

        {isAuthenticated && (
          <div className="mb-8">
            <SubscriptionStatus />
          </div>
        )}
        <div id="planos" className="scroll-mt-20" />
        <PricingSection />

        {/* CTA de dúvida — encoraja contato humano em vez de abandonar */}
        <div className="mt-8 text-center">
          <a
            href="https://wa.me/5573981988263?text=Ol%C3%A1!%20Estou%20na%20p%C3%A1gina%20de%20planos%20e%20tenho%20uma%20d%C3%BAvida%20antes%20de%20assinar."
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              track("whatsapp_click", {
                origin: "plans_page",
                intent: "duvida",
                page: "/planos",
              })
            }
          >
            <Button variant="outline" className="border-green-500 text-green-700 hover:bg-green-50 font-semibold">
              💬 Tenho dúvidas antes de assinar
            </Button>
          </a>
          <p className="text-xs text-muted-foreground mt-2">
            Fale com um atendente humano no WhatsApp — respondemos rapidinho.
          </p>
        </div>

        <div className="mt-12 text-center">
          <h3 className="text-2xl font-semibold mb-4">Garantia de Satisfação</h3>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Oferecemos 7 dias de garantia em todos os planos pagos. 
            Se não ficar satisfeito, devolvemos seu dinheiro sem perguntas.
          </p>
        </div>

        <div className="mt-8 text-center">
          <h3 className="text-xl font-semibold mb-4">Métodos de Pagamento Aceitos</h3>
          <p className="text-muted-foreground">
            Aceitamos cartão de crédito, débito, Pix e boleto através do Stripe.
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default PlansPage;
